import React, {
  useEffect,
  useRef,
} from "react";
import { StewartDialog } from "controls/global/dialogs/stewart-dialog/StewartDialog";
import {
  Box,
  IconButton,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Calculate, Lock } from "@mui/icons-material";
import {
  FormProvider,
  useForm,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  borderRadius,
  colors,
  fontSize,
  fontWeight,
  gaps,
  iconSize,
  margin,
  padding,
} from "theme/defaultStyle";
import CurrencyInputField from "controls/global/currency-field/CurrencyInputField";
import ActionButton from "controls/global/action-button/ActionButton";
import { showToast } from "controls/global/stewart-toast";
import { PolicyManagementRecord } from "entities/UIModel/PolicyManagementRecord";
import { savePolicyTransaction } from "utils/api/policyTransactionApi";
import { BookedRow } from "utils/data/enum";
import {
  expectedRemittance,
  financialsDisplaySource,
  SPLIT_MISMATCH_FIELD_ERROR,
} from "./helper";
import PercentNumberFormat from "../shared/PercentNumberFormat";
import FieldError from "controls/global/field-error/FieldError";

const StyledDialog = styled(StewartDialog)({
  "& > .MuiDialog-container > .MuiDialog-paper": {
    maxHeight: "calc(95vh)",
    maxWidth: "760px",
    minWidth: "760px",
    display: "flex",
    justifyContent: "flex-start",
    padding: `${padding.xlarge21_5} ${padding.xlarge22} ${padding.xlarge22} ${padding.xlarge22}`,
    overflowX: "auto",
  },
});

const StyledDialogTitle = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  columnGap: gaps.large1,
  marginBottom: margin.large,
  "& .MuiTypography-root": {
    fontSize: fontSize.xlarge2,
    fontWeight: fontWeight.bold2,
  },
});

const IconContainer = styled("div")({
  background: colors.blue19,
  borderRadius: borderRadius.large,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "56px",
  height: "56px",
  "& .MuiSvgIcon-root": {
    color: colors.blue15,
    fontSize: iconSize.medium2,
  },
});

const StyledDescriptionTypography = styled(Typography)({
  color: colors.grey22,
  fontSize: fontSize.small,
});

const CloseIconContainer = styled("div")({
  background: colors.grey10,
  borderRadius: borderRadius.xlarge1,
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  marginLeft: "auto",
  marginBottom: "auto",
  width: "32px",
  height: "32px",
  padding: "4px",
  "& .MuiSvgIcon-root": {
    color: colors.blue01,
    width: "24px",
    height: "24px",
  },
  "& .iconButton": {
    padding: padding.zero,
    backgroundColor: colors.grey10,
  },
});

const StyledDialogContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.large1,
});

const SplitToolbar = styled(Box)({
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  flexWrap: "wrap",
  columnGap: gaps.large1,
  rowGap: gaps.small1,
  background: colors.blue20,
  border: `1px solid ${colors.blue21}`,
  borderRadius: borderRadius.medium,
  padding: `${padding.large1} ${padding.xlarge}`,
});

const ToolbarBlock = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.xsmall1,
});

const FieldLabel = styled(Typography)({
  fontSize: fontSize.xsmall,
  fontWeight: fontWeight.bold2,
  color: colors.blue15,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
});

const OriginalSplitValue = styled(Typography)({
  fontSize: fontSize.large,
  fontWeight: fontWeight.bold2,
  color: colors.grey08,
});

const HelperText = styled(Typography)({
  fontSize: fontSize.small,
  color: colors.grey11,
  flex: 1,
  minWidth: "200px",
  textAlign: "left",
});

const SectionHeader = styled(Typography)({
  fontSize: fontSize.small,
  fontWeight: fontWeight.bold2,
  color: colors.blue01,
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  marginBottom: margin.xlarge,
});

const FieldsGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr 1fr",
  gap: gaps.large1,
});

// One grid cell: the textbox with its validation message directly beneath it.
// Hides CurrencyInputField's built-in absolutely-positioned ErrorMessage, which
// isn't anchored to its own field, in favour of the in-flow line below.
const FieldCell = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.xsmall1,
  minWidth: 0, // grid/flex items default to min-width:auto, which defeats the ellipsis
  "& .errorMessage": { display: "none" },
});

const DialogFooter = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: gaps.small1,
  marginTop: margin.large,
  paddingTop: padding.large1,
  borderTop: `1px solid ${colors.grey09}`,
});

const OVERRIDE_SAVE_SUCCESS = "Override successful. The new financials will display on the current booked row.";
const OVERRIDE_SAVE_ERROR = "Failed to save the override. Please try again.";
const ENTER_NEW_SPLIT_FIRST = "Enter a New Split % first";
const SPLIT_RANGE_ERROR = "Split % must be between 0 and 100";
const REMITTANCE_OVER_PREMIUM_ERROR = "Underwriter Remittance cannot exceed Premium/Fee";
const TAX_OVER_PREMIUM_ERROR = "Tax / State Fee cannot exceed Premium/Fee";

const DESCRIPTION_PREFIX = "Override Financials is a separate dataset that applies to whichever row is currently ";
const DESCRIPTION_BOLD = "booked";
const DESCRIPTION_SUFFIX = ". The header below shows the row your override will display on.";
const HELPER_PREFIX = "Computes ";
const HELPER_BOLD = "UW Remittance";
const HELPER_SUFFIX = " as Premium × New Split %. Premium stays unchanged (gross).";
const CALCULATED_SOURCE_TEXT = "Sourced from STRAFE (Booked)";
const REPORTED_SOURCE_TEXT = "Sourced from agent submission (Booked)";

const PercentField = styled(TextField)({
  width: "140px",
  background: colors.white,
  "& .MuiInputBase-input": {
    textAlign: "left",
  },
});

export interface OverrideFinancials {
  newSplitPercent: string;
  premiumFee: string;
  underwriterRemittance: string;
  taxStateFee: string;
  reissueLiability: string;
  siLiability: string;
  additionalLiability: string;
}

// Currency/percent fields hold strings (or 0 when cleared), so compare as numbers.
const toAmount = (val?: string | number | null) => Number(val ?? 0) || 0;

const isSameAmount = (
  a?: string | number | null,
  b?: string | number | null,
) => Math.round(toAmount(a) * 100) === Math.round(toAmount(b) * 100);

// Split % the derive effect settles on for a given premium/remittance pair.
const splitFor = (premium: number, remittance: number) =>
  Math.min(100, Math.max(0, (remittance / premium) * 100)).toFixed(2);

const overrideFinancialsSchema = yup.object({
  newSplitPercent: yup
    .string()
    .test("split-range", SPLIT_RANGE_ERROR, (val) => {
      if (!val) return true; // empty is handled by canOverride
      const parsed = Number(val);
      return !isNaN(parsed) && parsed >= 0 && parsed <= 100;
    }),
  underwriterRemittance: yup
    .string()
    .test("remittance-lte-premium", REMITTANCE_OVER_PREMIUM_ERROR, function (val) {
      return toAmount(val) <= toAmount(this.parent.premiumFee);
    }),
  taxStateFee: yup
    .string()
    .test("tax-lte-premium", TAX_OVER_PREMIUM_ERROR, function (val) {
      return toAmount(val) <= toAmount(this.parent.premiumFee);
    }),
});

type Props = {
  open: boolean;
  onClose: () => void;
  record: PolicyManagementRecord;
  isReported: boolean;
  originalSplitPercent?: number;
  onOverride?: (values: OverrideFinancials) => void;
  onRefresh?: () => void;
};

const getDefaultValues = (
  initialValues?: Partial<OverrideFinancials>
): OverrideFinancials => ({
  newSplitPercent: "",
  premiumFee: "0",
  underwriterRemittance: "0",
  taxStateFee: "0",
  reissueLiability: "0",
  siLiability: "0",
  additionalLiability: "0",
  ...initialValues,
});

const OverrideFinancialsModal = ({
  open,
  onClose,
  record,
  onOverride,
  isReported,
  onRefresh,
}: Props) => {

  const bookedRow = isReported ? BookedRow.REPORTED : BookedRow.CALCULATED;
  const displayed = financialsDisplaySource(record, bookedRow)[
    isReported ? "reported" : "calculated"
  ];

  const initialValues = {
    newSplitPercent: record.NewRemitSplitPct?.toString() || "",
    premiumFee: displayed.fee?.toString() || "0",
    underwriterRemittance: displayed.remittance?.toString() || "0",
    taxStateFee: displayed.tax?.toString() || "0",
    reissueLiability: record.ReissueLiabilityAmount?.toString() || "0",
    siLiability: record.SILiabilityAmount?.toString() || "0",
    additionalLiability: record.AdditionalLiabilityAmount?.toString() || "0",
  };

  const methods = useForm<OverrideFinancials>({
    mode: "onBlur",
    defaultValues: getDefaultValues(initialValues),
    resolver: yupResolver(overrideFinancialsSchema),
  });
  const {
    reset,
    getValues,
    setValue,
    watch,
    trigger,
    setError,
    formState,
  } = methods;

  const values = watch();
  const { newSplitPercent, premiumFee, underwriterRemittance, taxStateFee } =
    values;

  const openingValues = useRef<OverrideFinancials>();

  const isUnchanged = () =>
    !openingValues.current ||
    (Object.keys(openingValues.current) as (keyof OverrideFinancials)[]).every(
      (field) => isSameAmount(values[field], openingValues.current?.[field]),
    );

  const parsedNewSplitPercent = Number(newSplitPercent);
  const parsedPremiumFee = Number(premiumFee);
  const parsedRemittance = Number(underwriterRemittance);
  const parsedTax = Number(taxStateFee) || 0;

  // mode: "onBlur" alone would leave the remittance / tax errors stale when
  // Premium changes, since those fields aren't the ones being blurred.
  useEffect(() => {
    trigger(["taxStateFee"]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedPremiumFee]);

  // Derived split, with .toFixed(2) to match the form field precision and avoid false positives.
  const derivedSplit =
    parsedPremiumFee > 0 ? splitFor(parsedPremiumFee, parsedRemittance) : null;

  // Operator can manually enter split % but dollars won't auto-recalculate (only Calculate button does).
  // Compare in hundredths to match how split is stored/displayed.
  const splitMismatch = () =>
    derivedSplit !== null &&
    Math.round(Number(derivedSplit) * 100) !==
      Math.round(parsedNewSplitPercent * 100);

  // Clear mismatch error on input change without wiping the resolver's remittance-lte-premium error.
  useEffect(() => {
    trigger("underwriterRemittance");
  }, [newSplitPercent, premiumFee, underwriterRemittance, trigger]);

  useEffect(() => {
    if (open) {
      const opening = getDefaultValues(initialValues);
      reset(opening);
      openingValues.current = opening;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleOnClose() {
    onClose && onClose();
  }

  // Remittance = Premium x New Split %. Premium stays unchanged (gross).
  const handleCalculate = () => {
    const parsedSplit = Number(getValues("newSplitPercent")) || 0;
    const split = parsedSplit > 100 ? 100 : parsedSplit;
    if (split <= 0) {
      showToast({
        message: ENTER_NEW_SPLIT_FIRST,
        type: "info",
      });
      return;
    }

    const premium = Number(getValues("premiumFee")) || 0;
    setValue(
      "underwriterRemittance",
      expectedRemittance(premium, split).toFixed(2),
    );
  };

  const handleOverride = async () => {
    // Check here (not in canOverride) so the operator gets an explanation if submit fails.
    if (splitMismatch()) {
      // Flag on remittance, not split %: operator declared the split they want; remittance is what disagrees.
      setError("underwriterRemittance", {
        message: SPLIT_MISMATCH_FIELD_ERROR(
          parsedNewSplitPercent,
          parsedPremiumFee,
          expectedRemittance(parsedPremiumFee, parsedNewSplitPercent),
        ),
      });
      return;
    }

    onOverride && onOverride(getValues());

    // const calculatedOverrideFields = {
    //   CalculatedFee: Number(getValues("premiumFee")),
    //   CalculatedPremiumTax: Number(getValues("taxStateFee")),
    //   CalculatedRemittance: Number(getValues("underwriterRemittance")),
    // }

    const actualOverrideFields = {
      ActualFee: Number(getValues("premiumFee")),
      ActualPremiumTax: Number(getValues("taxStateFee")),
      ActualRemittance: Number(getValues("underwriterRemittance")),
    }

    const isOverridden = await savePolicyTransaction({
      IdentityKey: record.IdentityKey,
      IsOverride: true,
      BookedTypeCode: isReported ? BookedRow.REPORTED : BookedRow.CALCULATED,
      NewRemitSplitPct: Number(getValues("newSplitPercent")),
      ReissueLiabilityAmount: Number(getValues("reissueLiability")),
      SILiabilityAmount: Number(getValues("siLiability")),
      OverrideDate: new Date().toISOString(),
      AdditionalLiabilityAmount: Number(getValues("additionalLiability")),
      // Always store in ActualFields regardless of bookedType
      // ...(isReported ? actualOverrideFields : calculatedOverrideFields),
      ...actualOverrideFields,
    });

    if (isOverridden) {
      showToast({
        message: OVERRIDE_SAVE_SUCCESS,
        type: "success",
      });
      handleOnClose();
      onRefresh?.();
    } else {
      showToast({
        message: OVERRIDE_SAVE_ERROR,
        type: "error",
      });
    }
  };

  const canOverride =
    !isUnchanged() &&
    parsedNewSplitPercent > 0 &&
    parsedPremiumFee > 0 &&
    parsedRemittance > 0 &&
    parsedNewSplitPercent <= 100 &&
    parsedRemittance <= parsedPremiumFee &&
    parsedTax <= parsedPremiumFee;

  useEffect(() => {
    if(parsedPremiumFee <= 0) return;
    setValue("newSplitPercent", splitFor(parsedPremiumFee, parsedRemittance));
  }, [
    parsedPremiumFee,
    parsedRemittance,
    setValue
  ])

  return (
    <StyledDialog open={open}>
      <FormProvider {...methods}>
        <StyledDialogTitle>
          <IconContainer>
            <Lock />
          </IconContainer>
          <Typography>Override Financials</Typography>
          <CloseIconContainer>
            <IconButton className="iconButton" onClick={handleOnClose}>
              <CloseIcon />
            </IconButton>
          </CloseIconContainer>
        </StyledDialogTitle>

        <StyledDialogContent>
          <StyledDescriptionTypography>
            {DESCRIPTION_PREFIX}
            <strong style={{ color: colors.grey08 }}>{DESCRIPTION_BOLD}</strong>
            {DESCRIPTION_SUFFIX}
          </StyledDescriptionTypography>

          <SplitToolbar>
            <ToolbarBlock>
              <FieldLabel sx={{ color: colors.blue15 }}>Original Split %</FieldLabel>
              <OriginalSplitValue>
                {record.OriginalRemitSplitPct?.toFixed(2) || "0"}%
              </OriginalSplitValue>
            </ToolbarBlock>

            <ToolbarBlock>
              <PercentField
                label="NEW SPLIT %"
                size="small"
                variant="outlined"
                value={newSplitPercent}
                onChange={(e) => setValue("newSplitPercent", e.target.value)}
                error={Boolean(formState.errors.newSplitPercent)}
                InputLabelProps={{ shrink: Boolean(newSplitPercent) || undefined }}
                InputProps={{
                  inputComponent: PercentNumberFormat as any,
                  inputProps: { name: "newSplitPercent", suffix: " %"},
                }}
              />
              <FieldError message={formState.errors.newSplitPercent?.message} />
            </ToolbarBlock>

            <ActionButton
              variant="contained"
              color="primary"
              startIcon={<Calculate />}
              onClick={handleCalculate}
            >
              Calculate
            </ActionButton>

            <HelperText>
              {HELPER_PREFIX}
              <strong>{HELPER_BOLD}</strong>
              {HELPER_SUFFIX}
            </HelperText>
          </SplitToolbar>

          <Box>
            <SectionHeader>
              {isReported ? "Reported ":  "Calculated "}
              <Box
                component="span"
                sx={{
                  color: colors.grey11,
                  fontWeight: fontWeight.normal2,
                  textTransform: "none",
                  letterSpacing: 0,
                }}
              >
                {isReported ? REPORTED_SOURCE_TEXT : CALCULATED_SOURCE_TEXT}
              </Box>
            </SectionHeader>
            <FieldsGrid>
              <FieldCell>
                <CurrencyInputField label="PREMIUM/FEE" name="premiumFee" />
                <FieldError message={formState.errors.premiumFee?.message} />
              </FieldCell>
              <FieldCell>
                <CurrencyInputField
                  label="UNDERWRITER REMITTANCE"
                  name="underwriterRemittance"
                />
                <FieldError
                  message={formState.errors.underwriterRemittance?.message}
                />
              </FieldCell>
              <FieldCell>
                <CurrencyInputField label="TAX / STATE FEE" name="taxStateFee" />
                <FieldError message={formState.errors.taxStateFee?.message} />
              </FieldCell>
            </FieldsGrid>
          </Box>

          <Box>
            <SectionHeader>Other</SectionHeader>
            <FieldsGrid>
              <CurrencyInputField
                label="Reissue Liability"
                name="reissueLiability"
              />
              <CurrencyInputField label="SI Liability" name="siLiability" />
              <CurrencyInputField
                label="Additional Liability"
                name="additionalLiability"
              />
            </FieldsGrid>
          </Box>

          <DialogFooter>
            <ActionButton
              variant="outlined"
              color="secondary"
              onClick={handleOnClose}
            >
              Cancel
            </ActionButton>
            <ActionButton
              variant="contained"
              color="primary"
              disabled={!canOverride}
              onClick={handleOverride}
            >
              Override Financials
            </ActionButton>
          </DialogFooter>
        </StyledDialogContent>
      </FormProvider>
    </StyledDialog>
  );
};

export default OverrideFinancialsModal;
